import {
  useEffect,
  useLayoutEffect,
  useState,
  MutableRefObject,
  MouseEvent,
  MouseEventHandler,
  useMemo,
} from 'react';

import { RLottieHandler } from '@gem/modules/rlottie';

import { IReactionItem } from 'APP/main/hooks/useReactions/useReactions.types';
import { useAnimateSticker } from 'APP/packages/stickerAnimation/useAnimateSticker';

interface IReactionItemParams {
  reaction: IReactionItem;
  isAnimated: boolean;
  isAnimatedLoop: boolean;
  isVisible: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
}

interface IReactionItemPresenter {
  onClick(event: MouseEvent<HTMLButtonElement>): void;
  onHoverIn(): void;
  onHoverOut(): void;
  hasMyReaction: boolean;
  counter: string;
}

interface IUseReactionItemPresenter {
  presenter: IReactionItemPresenter;
  stickerContainerRef: MutableRefObject<HTMLSpanElement | null>;
}

const onStopAnimation = (handler: RLottieHandler): void => {
  handler.reset();
};

export const useReactionItemPresenter = (data: IReactionItemParams): IUseReactionItemPresenter => {
  const { reaction, isAnimated, isAnimatedLoop, onClick, isVisible } = data;

  const [isHover, setIsHover] = useState(false);
  const { animationRef, stickerContainerRef } = useAnimateSticker<HTMLSpanElement>({
    autoplay: false,
    loop: false,
    url: reaction.animationUrl,
    onLoopComplete: onStopAnimation,
  });

  const [hasMyReaction, setHasMyReaction] = useState(reaction.hasMyReaction);
  const [counter, setCounter] = useState(reaction.counter);

  const presenter: IReactionItemPresenter = useMemo(
    () => ({
      onClick(event: MouseEvent<HTMLButtonElement>): void {
        onClick ? onClick(event) : reaction.onClick();
      },

      onHoverIn(): void {
        setIsHover(true);
      },

      onHoverOut(): void {
        setIsHover(false);
      },

      get hasMyReaction(): boolean {
        return hasMyReaction;
      },

      get counter(): string {
        return counter;
      },
    }),
    [reaction.id, animationRef, hasMyReaction, counter]
  );

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        animationRef.current.destroy();
        animationRef.current = undefined;
      }
    };
  }, [reaction.id, animationRef]);

  useEffect(() => {
    animationRef.current?.updateIsVisible(isVisible);
  }, [isVisible]);

  useLayoutEffect(() => {
    if (isAnimated || isAnimatedLoop || isHover) {
      if (!animationRef.current?.playing) {
        animationRef.current?.play();
      }
    } else {
      if (animationRef.current?.playing) {
        animationRef.current?.pause();
        animationRef.current?.reset();
      }
    }
  }, [isAnimated, isAnimatedLoop, isHover]);

  useLayoutEffect(() => {
    animationRef.current?.setLoop(isAnimatedLoop || isHover);
  }, [isAnimatedLoop, isHover]);

  useEffect(() => {
    setHasMyReaction(reaction.hasMyReaction);
    setCounter(reaction.counter);
  }, [reaction.hasMyReaction, reaction.counter]);

  return { stickerContainerRef, presenter };
};
